import { create } from "zustand";
import { fileContentHash, RivetsApiError } from "@rivets/core";
import { rereadFile, writeFile } from "@/lib/dataSource";
import { workerAccessErrorText } from "@/features/workers/worker-access";

/**
 * Unsaved edits to worktree files, per worktree and path.
 *
 * A draft belongs to neither the tab nor the page showing it: switching tabs,
 * closing one or moving to another task leaves it exactly as it was, and
 * only Save or Revert ends it. Mirrors the native `FileDraft`.
 */
export type FileDraft = {
  /** The file as it was when the edit began: what Revert returns to, and what
   * a save checks the disk against before writing. */
  base: string;
  text: string;
  /** A save found the file changed on disk since `base`. */
  conflict?: boolean;
  saving?: boolean;
  error?: string;
};

type FileDraftsState = {
  drafts: Record<string, Record<string, FileDraft>>;
  /** The account the drafts were typed under. */
  ownerId: string | null;
  /** Drops every draft when a different account signs in: one person's
   * unsaved edits are never shown to — or saved as — another. */
  scopeTo: (userId: string) => void;
  reset: () => void;
  /** Records an edit. Typing back to exactly the base ends the draft. */
  edit: (worktreeId: string, path: string, base: string, text: string) => void;
  discard: (worktreeId: string, path: string) => void;
  patch: (worktreeId: string, path: string, patch: Partial<FileDraft>) => void;
};

/** One draft, by own property only: a path is user data, and a file named
 * `constructor` or `__proto__` must not find something on `Object.prototype`. */
export function draftAt(
  drafts: FileDraftsState["drafts"],
  worktreeId: string,
  path: string,
): FileDraft | undefined {
  const files = Object.hasOwn(drafts, worktreeId) ? drafts[worktreeId] : undefined;
  return files && Object.hasOwn(files, path) ? files[path] : undefined;
}

/** Whether `path` in the worktree has unsaved edits. */
export function hasFileDraft(worktreeId: string, path: string): boolean {
  return draftAt(useFileDrafts.getState().drafts, worktreeId, path) !== undefined;
}

function withDraft(
  drafts: FileDraftsState["drafts"],
  worktreeId: string,
  path: string,
  draft: FileDraft | null,
): FileDraftsState["drafts"] {
  const { [path]: _previous, ...others } = Object.hasOwn(drafts, worktreeId) ? drafts[worktreeId]! : {};
  const files = draft ? { ...others, [path]: draft } : others;
  if (Object.keys(files).length > 0) return { ...drafts, [worktreeId]: files };
  const { [worktreeId]: _removed, ...rest } = drafts;
  return rest;
}

export const useFileDrafts = create<FileDraftsState>()((set) => ({
  drafts: {},
  ownerId: null,
  scopeTo: (userId) => set((state) => {
    if (state.ownerId === userId) return state;
    return { ownerId: userId, drafts: state.ownerId === null ? state.drafts : {} };
  }),
  reset: () => set({ drafts: {}, ownerId: null }),
  edit: (worktreeId, path, base, text) => set((state) => {
    const current = draftAt(state.drafts, worktreeId, path);
    if (current?.text === text) return state;
    const draftBase = current?.base ?? base;
    // A draft with a save in flight stays even when typed back to its base:
    // the save is about to replace that base, and the undo must survive it.
    if (text === draftBase && !current?.conflict && !current?.saving) return { drafts: withDraft(state.drafts, worktreeId, path, null) };
    return { drafts: withDraft(state.drafts, worktreeId, path, { ...current, base: draftBase, text, error: undefined }) };
  }),
  discard: (worktreeId, path) => set((state) => ({ drafts: withDraft(state.drafts, worktreeId, path, null) })),
  patch: (worktreeId, path, patch) => set((state) => {
    const current = draftAt(state.drafts, worktreeId, path);
    return current ? { drafts: withDraft(state.drafts, worktreeId, path, { ...current, ...patch }) } : state;
  }),
}));

const NO_DRAFTS: Record<string, FileDraft> = {};

export function useFileDraft(worktreeId: string, path: string): FileDraft | undefined {
  return useFileDrafts((state) => draftAt(state.drafts, worktreeId, path));
}

/** The worktree's files with unsaved edits, keyed by path. */
export function useWorktreeFileDrafts(worktreeId: string): Record<string, FileDraft> {
  return useFileDrafts((state) => (Object.hasOwn(state.drafts, worktreeId) ? state.drafts[worktreeId]! : NO_DRAFTS));
}

export function hasUnsavedFileDrafts(): boolean {
  return Object.keys(useFileDrafts.getState().drafts).length > 0;
}

/** CodeMirror joins lines with `\n` whatever the file used; a file written
 * with `\r\n` is saved back that way. */
export function lineBreakOf(text: string): "\n" | "\r\n" {
  return text.includes("\r\n") ? "\r\n" : "\n";
}

export function withLineBreak(text: string, lineBreak: "\n" | "\r\n"): string {
  return lineBreak === "\n" ? text : text.replace(/\r?\n/g, "\r\n");
}

/**
 * Writes a draft to the worktree. Without `overwrite` the worker refuses a
 * file that no longer matches the draft's base, which flags the draft as a
 * conflict instead. Edits typed while the save was in flight stay a draft, now
 * based on what was saved. A failed save keeps the draft whatever it holds: a
 * lost reply may still have written the file.
 */
export async function saveFileDraft(worktreeId: string, path: string, { overwrite = false } = {}): Promise<boolean> {
  const store = useFileDrafts.getState();
  const draft = draftAt(store.drafts, worktreeId, path);
  if (!draft || draft.saving) return false;
  const saved = draft.text;
  const owner = store.ownerId;
  store.patch(worktreeId, path, { saving: true, error: undefined });
  try {
    const expectedHash = overwrite ? null : await fileContentHash(draft.base);
    const stillCurrent = () => useFileDrafts.getState().ownerId === owner;
    // Hashing awaited: an account that signed in meanwhile must not send it.
    if (!stillCurrent()) return false;
    await writeFile(worktreeId, path, saved, expectedHash, stillCurrent);
  } catch (error) {
    // Another account took over meanwhile; nothing of this save is theirs.
    if (useFileDrafts.getState().ownerId !== owner) return false;
    const conflict = error instanceof RivetsApiError && error.code === "file_conflict";
    useFileDrafts.getState().patch(worktreeId, path, {
      saving: false,
      conflict: conflict || undefined,
      error: conflict ? undefined : workerAccessErrorText(error) ?? (error instanceof Error ? error.message : "Could not save the file."),
    });
    return false;
  }
  if (useFileDrafts.getState().ownerId !== owner) return false;
  const latest = draftAt(useFileDrafts.getState().drafts, worktreeId, path);
  if (!latest || latest.text === saved) useFileDrafts.getState().discard(worktreeId, path);
  else useFileDrafts.getState().patch(worktreeId, path, { base: saved, saving: false, conflict: undefined });
  return true;
}

/** Throws the edit away and re-reads the file, which may have moved on. */
export function revertFileDraft(worktreeId: string, path: string): void {
  useFileDrafts.getState().discard(worktreeId, path);
  rereadFile(worktreeId, path);
}

// Unsaved edits live only in this page. Leaving or reloading it is the one
// thing that can lose them, so the browser is asked to confirm first.
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (event) => {
    if (!hasUnsavedFileDrafts()) return;
    event.preventDefault();
    event.returnValue = "";
  });
}
